import { ArrowLeft, ChevronDown, ChevronRight, History } from "lucide-react";
import { useState } from "react";
import { Link, Navigate } from "react-router-dom";

import { PageContainer } from "@/components/PageContainer";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { useAdminAudit } from "@/hooks/useAdminAudit";
import type { AuditEvent } from "@/hooks/useAdminAudit";
import { useMe } from "@/hooks/useMe";
import { isMaitimeStaff } from "@/lib/nav-config";
import { cn } from "@/lib/utils";

function formatDataOra(iso: string): string {
  return new Date(iso).toLocaleString("it-IT", {
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
}

export function AdminAuditPage() {
  const { data: me, isLoading: loadingMe } = useMe();
  const [evento, setEvento] = useState("");
  const [entitaTipo, setEntitaTipo] = useState("");
  const [giorni, setGiorni] = useState(7);

  const { data: events = [], isLoading } = useAdminAudit({
    evento: evento || undefined,
    entita_tipo: entitaTipo || undefined,
    giorni,
    limit: 200,
  });

  if (!loadingMe && me && !isMaitimeStaff(me)) {
    return <Navigate to="/" replace />;
  }

  return (
    <PageContainer>
      <Link
        to="/admin"
        className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors w-fit"
      >
        <ArrowLeft className="size-3.5" />
        Backoffice
      </Link>

      <header className="space-y-1">
        <p className="text-xs uppercase tracking-wider text-muted-foreground">
          Backoffice Maitime
        </p>
        <h1 className="text-3xl font-semibold tracking-tight text-foreground">
          Audit log
        </h1>
        <p className="text-sm text-muted-foreground max-w-2xl pt-1">
          Tracciabilità delle mutazioni applicative (bacheca, moduli, utenti, documenti, feed, ai_call). Filtri per evento, entità, periodo.
        </p>
      </header>

      <div className="rounded-lg border bg-card p-3 flex flex-wrap gap-3 items-end">
        <Field label="Evento (prefix)">
          <input
            type="text"
            value={evento}
            onChange={(e) => setEvento(e.target.value)}
            placeholder="es: bacheca, documento.caricato"
            className="rounded-md border bg-background px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring w-56"
          />
        </Field>
        <Field label="Entità tipo">
          <input
            type="text"
            value={entitaTipo}
            onChange={(e) => setEntitaTipo(e.target.value)}
            placeholder="es: project.documento"
            className="rounded-md border bg-background px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring w-56"
          />
        </Field>
        <Field label="Periodo">
          <select
            value={giorni}
            onChange={(e) => setGiorni(parseInt(e.target.value, 10))}
            className="rounded-md border bg-background px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
          >
            <option value={1}>Ultimo giorno</option>
            <option value={7}>Ultima settimana</option>
            <option value={30}>Ultimo mese</option>
            <option value={90}>Ultimi 3 mesi</option>
            <option value={180}>Ultimi 6 mesi</option>
          </select>
        </Field>
      </div>

      {isLoading ? (
        <Skeleton className="h-96 w-full" />
      ) : events.length === 0 ? (
        <div className="rounded-lg border bg-card/40 p-8 text-center">
          <History className="size-8 mx-auto text-muted-foreground mb-2" />
          <p className="text-sm text-muted-foreground">
            Nessun evento nel periodo selezionato.
          </p>
        </div>
      ) : (
        <div className="rounded-lg border bg-card overflow-hidden">
          <table className="w-full text-xs">
            <thead className="bg-muted/40 text-muted-foreground">
              <tr>
                <th className="text-left font-medium px-3 py-2 w-[40px]"></th>
                <th className="text-left font-medium px-3 py-2 w-[140px]">Quando</th>
                <th className="text-left font-medium px-3 py-2">Evento</th>
                <th className="text-left font-medium px-3 py-2 w-[160px]">Entità</th>
                <th className="text-left font-medium px-3 py-2 w-[200px]">Attore</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {events.map((ev) => (
                <EventRow key={ev.id} event={ev} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </PageContainer>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1">
      <label className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">
        {label}
      </label>
      {children}
    </div>
  );
}

function EventRow({ event: ev }: { event: AuditEvent }) {
  const [open, setOpen] = useState(false);
  const hasPayload = ev.valore_prima || ev.valore_dopo || ev.metadati;
  return (
    <>
      <tr
        className={cn(
          "hover:bg-muted/20 cursor-pointer",
          open && "bg-muted/30"
        )}
        onClick={() => hasPayload && setOpen((v) => !v)}
      >
        <td className="px-3 py-2 text-muted-foreground">
          {hasPayload &&
            (open ? (
              <ChevronDown className="size-3.5" />
            ) : (
              <ChevronRight className="size-3.5" />
            ))}
        </td>
        <td className="px-3 py-2 text-[11px] text-muted-foreground whitespace-nowrap tabular-nums">
          {formatDataOra(ev.creato_il)}
        </td>
        <td className="px-3 py-2 font-mono">
          <Badge variant="outline" className="text-[10px]">
            {ev.evento}
          </Badge>
        </td>
        <td className="px-3 py-2 text-[11px] text-muted-foreground">
          <div className="font-mono">{ev.entita_tipo}</div>
          <div className="font-mono text-[10px] opacity-70">
            {ev.entita_id.substring(0, 8)}
          </div>
        </td>
        <td className="px-3 py-2 text-[11px] font-mono text-muted-foreground">
          {ev.attore_id ? ev.attore_id.substring(0, 8) : "—"}
        </td>
      </tr>
      {open && hasPayload && (
        <tr className="bg-muted/20">
          <td colSpan={5} className="px-3 py-3">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-[11px]">
              <PayloadBox label="Prima" data={ev.valore_prima} />
              <PayloadBox label="Dopo" data={ev.valore_dopo} />
              {ev.metadati && <PayloadBox label="Metadati" data={ev.metadati} />}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

function PayloadBox({
  label,
  data,
}: {
  label: string;
  data: Record<string, unknown> | null;
}) {
  if (!data || Object.keys(data).length === 0) return null;
  return (
    <div className="rounded-md border bg-card p-2">
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium mb-1">
        {label}
      </div>
      <pre className="font-mono text-[11px] whitespace-pre-wrap break-words overflow-x-auto">
        {JSON.stringify(data, null, 2)}
      </pre>
    </div>
  );
}
